import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GameOptions from './GameOptions';
import SceneDisplay from './SceneDisplay';
import UserInput from './UserInput';
import RatingDisplay from './RatingDisplay';

export default function Play({ advancedOptions, presetOptions, genreOptions }) {
    const [gameState, setGameState] = useState('options'); // 'options', 'playing', 'finished'
    const [scene, setScene] = useState(null);
    const [rating, setRating] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [imageSrc, setImageSrc] = useState('');

    useEffect(() => {
        // Set random image on component mount
        const num = Math.floor(Math.random() * 9) + 1;
        setImageSrc(`/game/sp${num}.png`); // Assuming images are in the public/game directory

        // Apply body styles
        document.body.style.backgroundColor = 'black';
        document.body.style.color = 'white';

        // Cleanup function to reset styles when component unmounts
        return () => {
            document.body.style.backgroundColor = '';
            document.body.style.color = '';
        };
    }, []);


    const handleStartGame = async (options) => {
        setIsLoading(true);
        setError(null);
        let endpoint = '';
        let payload = {};
        let headers = { 'Content-Type': 'application/json' };

        if (options.mode === 'simple') {
            endpoint = '/game/get_opening_scene.php';
            payload = { genre: options.genre };
        } else if (options.mode === 'advanced') {
            endpoint = '/game/get_opening_scene_advanced.php';
            headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
            payload = new URLSearchParams({ genre: options.genre, goal: options.goal });
        } else if (options.mode === 'preset') {
            endpoint = '/game/get_opening_scene_preset.php';
            payload = { scene: options.scene };
        }

        try {
            const response = await axios.post(endpoint, payload, { headers });
            setScene(response.data);
            setGameState('playing');
        } catch (err) {
            setError('Failed to start game. Please check the backend connection.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmitTurn = async (additions) => {
        if (!scene?.uid) return;
        setIsLoading(true);
        setError(null);
        try {
            const payload = { uid: scene.uid, additions };
            const response = await axios.post('/game/get_scene_response.php', payload);
            const aiResponseElement = { timestamp: new Date().toISOString(), actor: 'ai', type: 'response', content: response.data };
            setScene(prev => ({ ...prev, scene_elements: [...prev.scene_elements, ...additions, aiResponseElement] }));
        } catch (err) {
            setError('Failed to submit turn.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEndScene = async (additions) => {
        if (!scene?.uid) return;
        setIsLoading(true);
        setError(null);
        try {
            const payload = { uid: scene.uid, additions };
            const response = await axios.post('/game/end_scene.php', payload);
            setRating(response.data);
            if (additions.length > 0) {
                setScene(prev => ({ ...prev, scene_elements: [...prev.scene_elements, ...additions] }));
            }
            setGameState('finished');
        } catch (err) {
            setError('Failed to end scene.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePlayAgain = () => {
        setGameState('options');
        setScene(null);
        setRating(null);
        setError(null);
    };

    return (
        <>
            <style>{`
                .instructions-fieldset {
                    border-radius: 8px;
                    border: 3px solid teal;
                    padding: 5px;
                    color: white;
                }
                .instructions-legend {
                    all: unset;
                    color: teal;
                    padding: 3px;
                    font-size: 1.5em;
                }
                .computer {
                    background-color: black;
                    color: lime;
                    font-family: 'Courier New', Courier, monospace;
                    padding: 20px;
                    white-space: pre;
                    border-radius: 10px;
                }
                .cursor {
                    animation: blink .7s step-end infinite;
                }
                @keyframes blink {
                    from, to { color: transparent; }
                    50% { color: lime; }
                }
                .userText { color: #f4315c; }
                .serverText { color: lime; }
                body { color: white; }
            `}</style>
            <div className="container py-5">
                 <div className="row">
                    <div style={{color: '#caced2'}} className="col-md-7 d-flex align-items-center justify-content-center">
                        <div className="text-center">
                            <h2 className="computer">SceneProv<span className="cursor">_</span></h2>
                            <br />
                            <h5>Where the AI plays with you... and judges you.</h5>
                            <br />
                            <h5>Learn a little screenwriting. It won't start a <br /><span style={{fontFamily: "'Courier New', Courier, monospace"}}>Global Thermo Nuclear Warfare</span></h5>
                        </div>
                    </div>
                    <div className="col-md-5 d-flex align-items-center justify-content-center">
                         {imageSrc && <img src={imageSrc} style={{maxWidth: '100%'}} alt="SceneProv" />}
                    </div>
                </div>
                <br />

                {error && <div className="alert alert-danger">{error}</div>}
                
                {gameState === 'options' && (
                    <GameOptions
                        onStart={handleStartGame}
                        isLoading={isLoading}
                        advancedOptions={advancedOptions}
                        presetOptions={presetOptions}
                        genreOptions={genreOptions}
                    />
                )}
                
                {gameState === 'playing' && scene && (
                    <>
                        <SceneDisplay sceneElements={scene.scene_elements} instructions={scene.instructions} />
                        <UserInput onSubmit={handleSubmitTurn} onEnd={handleEndScene} isLoading={isLoading} />
                    </>
                )}

                {gameState === 'finished' && (
                     <>
                        <h2 className="text-center mb-3">Scene Complete!</h2>
                        {scene && <SceneDisplay sceneElements={scene.scene_elements} />}
                        {rating && <RatingDisplay rating={rating} scene={scene} />}
                        <div className="text-center mt-4">
                            <button onClick={handlePlayAgain} className="btn btn-primary">Play Again</button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}


